/**
 * transcriptionService — thin wrappers over the word-level transcription
 * Cloud Functions. Business logic lives server-side; this just invokes the
 * callables and surfaces the result.
 */

import { httpsCallable } from 'firebase/functions'
import { doc, getDoc } from 'firebase/firestore'
import { functions, db } from '@/js/firebase'
import { logger } from '@/services/loggerService'

/**
 * Enqueue transcription of a podcast episode's full-episode review video. The
 * worker writes the plain text to episodeTranscripts/{projectId} and the status
 * to projects/{projectId}.episodeTranscriptStatus (which the caller watches).
 * @param {string} projectId  the episode project id
 * @param {string} reviewId   the primary-episode deliverable's review id (=== deliverableId)
 */
export async function enqueueEpisodeTranscription(projectId, reviewId) {
  const fn = httpsCallable(functions, 'enqueueEpisodeTranscription')
  const { data } = await fn({ projectId, reviewId })
  logger.info('[transcriptionService] enqueueEpisodeTranscription', { projectId, ...data })
  return data
}

/* Removed: getEpisodeTranscript(). Generation now reads the transcript doc
   server-side (it needs the word timings, not just the text), and nothing else
   wanted text-only. See functions/episodeContentGeneration/generateEpisodeContent.js */

/**
 * Read the full episode transcript record for display — plain text + timestamped
 * WebVTT + word-level data (powers the transcript drawer's readable/word-level
 * toggle, same as asset transcripts).
 * @returns {Promise<{text: string, vtt: string, words: Array, wordLevel: boolean, source: string}>}
 */
export async function getEpisodeTranscriptData(projectId) {
  const empty = { text: '', vtt: '', words: [], wordLevel: false, source: '' }
  if (!projectId) return empty
  const snap = await getDoc(doc(db, 'episodeTranscripts', projectId))
  if (!snap.exists()) return empty
  const d = snap.data()
  return {
    text: d.text || '',
    vtt: d.vtt || '',
    words: Array.isArray(d.words) ? d.words : [],
    wordLevel: !!d.wordLevel,
    source: d.source || '',
  }
}

/**
 * (Re)generate a word-level transcript for a single asset. Enqueues a job and
 * kicks the worker; the asset's `transcriptWordLevel` + transcriptTasks/{id}
 * status update live via onSnapshot.
 * @param {string} assetId
 * @param {{language?: string}} [opts]
 */
export async function enqueueTranscription(assetId, { language } = {}) {
  const fn = httpsCallable(functions, 'enqueueTranscription')
  const { data } = await fn({ assetId, language })
  logger.info('[transcriptionService] enqueueTranscription', { assetId, ...data })
  return data
}

/**
 * Bulk-backfill word-level transcripts for older videos lacking one
 * (super-admin only). Optionally scope to a company.
 * @param {{companyId?: string, limit?: number}} [opts]
 */
export async function enqueueTranscriptionBacklog({ companyId, limit } = {}) {
  const fn = httpsCallable(functions, 'enqueueTranscriptionBacklog')
  const { data } = await fn({ companyId, limit })
  logger.info('[transcriptionService] enqueueTranscriptionBacklog', { companyId, ...data })
  return data
}

/**
 * Admin utility status: transcription queue counts, failures, and Bunny videos
 * still missing a transcript (super-admin only). Optional company scope.
 * @param {{companyId?: string}} [opts]
 */
export async function getTranscriptionStatus({ companyId } = {}) {
  const fn = httpsCallable(functions, 'getTranscriptionStatus', { timeout: 120000 })
  const { data } = await fn({ companyId })
  return data
}

/**
 * Backfill a specific set of assets (super-admin) — e.g. the filtered subset.
 * @param {{assetIds: string[]}} opts
 */
export async function enqueueTranscriptionForAssets({ assetIds } = {}) {
  const fn = httpsCallable(functions, 'enqueueTranscriptionForAssets', { timeout: 300000 })
  const { data } = await fn({ assetIds })
  logger.info('[transcriptionService] enqueueTranscriptionForAssets', { count: assetIds?.length, ...data })
  return data
}

/** Run one drain pass now (super-admin). */
export async function kickTranscriptWorker() {
  const fn = httpsCallable(functions, 'kickTranscriptWorker', { timeout: 540000 })
  const { data } = await fn({})
  return data
}

/**
 * Clear failed transcription task(s) (super-admin). Pass an assetId to clear one,
 * or nothing to clear all failed tasks. The asset itself is untouched.
 * @param {{assetId?: string}} [opts]
 */
export async function clearFailedTranscriptTasks({ assetId } = {}) {
  const fn = httpsCallable(functions, 'clearFailedTranscriptTasks', { timeout: 120000 })
  const { data } = await fn({ assetId })
  return data
}
